import { Group } from "@mantine/core"
import { ColumnManager, ExportButton, ExportType, FilterBuilder } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { SortBuilder } from "~/components/SortBuilder"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import { ExportCertificatesPdfRequestDto } from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useBuildCertificatesFilters } from "~/pages/Brand/Certificates/components/hooks/useBuildCertificatesFilters"
import { useBuildCertificatesSearch } from "~/pages/Brand/Certificates/components/hooks/useBuildCertificatesSearch"
import { useBuildCertificatesSorts } from "~/pages/Brand/Certificates/components/hooks/useBuildCertificatesSorts"
import {
  CertificatesListView,
  CertificatesListViewSchema,
} from "~/pages/Brand/Certificates/constants"
import { useExportCertificatesPdfMutation } from "~/pages/Brand/Certificates/hooks/useExportCertificatesPdfMutation"
import { useCertificatesTableContext } from "~/pages/Brand/Certificates/provider/CertificatesTableContext"

export const CertificatesTableHeaders = () => {
  const { t } = useTranslation()
  const params = useParams()
  const view =
    CertificatesListViewSchema.safeParse(params.view).data ??
    CertificatesListView.ALL
  const tableContext =
    TABLE_CONTEXT_MAP[
      `CERTIFICATES_${view.toUpperCase()}` as keyof typeof TABLE_CONTEXT_MAP
    ]
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { rowSelection, table, isColumnManagerOpen, setIsColumnManagerOpen } =
    useCertificatesTableContext()
  const { filters, setFilters, options, title, searchPlaceholder } =
    useBuildCertificatesFilters()
  const { sorts, setSorts, sortOptions } = useBuildCertificatesSorts()
  const { search, setSearch, placeholder, isLoading } =
    useBuildCertificatesSearch()
  const { mutate: exportCertificates, isPending: isCertificateExportLoading } =
    useExportCertificatesPdfMutation()

  if (!table) return null

  return (
    <>
      <Views tableContext={tableContext} />
      <Group justify="space-between">
        <Group>
          <SearchBar
            filtersValue={search}
            setFiltersValue={setSearch}
            placeholder={placeholder}
            isFetching={isLoading}
          />
          <SortBuilder
            sorts={sorts}
            setSorts={setSorts}
            options={sortOptions}
          />
          <FilterBuilder
            filters={filters}
            setFilters={setFilters}
            options={options}
            title={title}
            searchPlaceholder={searchPlaceholder}
          />
          <ColumnManager
            table={table}
            translations={columnManagerTranslations}
            isOpen={isColumnManagerOpen}
            setIsOpen={setIsColumnManagerOpen}
          />
          <ViewHeader tableContext={tableContext} />
        </Group>
        <ExportButton<ExportCertificatesPdfRequestDto>
          label={t("common:actionButtons.export")}
          exportOptions={[
            {
              type: ExportType.PDF,
              label: t("common:actionButtons.exportAsPdf"),
              isLoading: isCertificateExportLoading,
              exportFunction: (args) => exportCertificates({ data: args }),
              args: {
                certificateIds: Object.keys(rowSelection).filter(
                  (row) => rowSelection[row]
                ),
              },
            },
          ]}
          count={Object.keys(rowSelection).length}
          disabled={Object.keys(rowSelection).length === 0}
        />
      </Group>
    </>
  )
}
